import React, {useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {RootState} from '../store';

import {text} from '../text';
import {hooks} from '../hooks';
import {theme} from '../constants';
import {components} from '../components';
import {svg} from '../assets/svg';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import {
  MdKeyboardArrowDown, MdCheckBox, MdCheckBoxOutlineBlank, MdAttachFile, MdDelete,
  MdClose, MdSearch, MdExpandMore, MdExpandLess
} from 'react-icons/md';
import { FaFileExcel, FaFileWord, FaFilePdf, FaFilePowerpoint, FaFileArchive, FaFileAlt, FaFileImage } from 'react-icons/fa';

const ArrowIcon = MdKeyboardArrowDown as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;
const AttachFileIcon = MdAttachFile as any;
const DeleteIcon = MdDelete as any;
const CloseIcon = MdClose as any;
const SearchIcon = MdSearch as any;
const ExpandMoreIcon = MdExpandMore as any;
const ExpandLessIcon = MdExpandLess as any;
const FileExcelIcon = FaFileExcel as any;
const FileWordIcon = FaFileWord as any;
const FilePdfIcon = FaFilePdf as any;
const FilePowerpointIcon = FaFilePowerpoint as any;
const FileArchiveIcon = FaFileArchive as any;
const FileAltIcon = FaFileAlt as any;
const FileImageIcon = FaFileImage as any;

const getFileIcon = (fileName: string) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '')) return { icon: <FileImageIcon size={40} color="#3B82F6" />, type: 'image' };
  if (['pdf'].includes(ext || '')) return { icon: <FilePdfIcon size={40} color="#EF4444" />, type: 'pdf' };
  if (['xls', 'xlsx', 'csv'].includes(ext || '')) return { icon: <FileExcelIcon size={40} color="#10B981" />, type: 'excel' };
  if (['doc', 'docx'].includes(ext || '')) return { icon: <FileWordIcon size={40} color="#3B82F6" />, type: 'word' };
  if (['ppt', 'pptx'].includes(ext || '')) return { icon: <FilePowerpointIcon size={40} color="#F59E0B" />, type: 'ppt' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) return { icon: <FileArchiveIcon size={40} color="#6B7280" />, type: 'archive' };
  return { icon: <FileAltIcon size={40} color="#9CA3AF" />, type: 'file' };
};

export const EditTask: React.FC = () => {
  const {state} = useLocation();
  const task = state?.task;
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const { t, language } = useLanguage();

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  // Form state — pre-filled from task
  const [description, setDescription] = useState<string>(task?.description || '');
  const [isUrgent, setIsUrgent] = useState<boolean>(task?.is_urgent || false);
  const [isMaintenance, setIsMaintenance] = useState<boolean>(task?.is_maintenance || false);
  const [selectedChecker, setSelectedChecker] = useState<string>(task?.checker?.id?.toString() || '');
  const [taskType, setTaskType] = useState<string>(task?.task_type || '');
  const [taskTypes, setTaskTypes] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [vessels, setVessels] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>(task?.client_id?.toString() || '');
  const [selectedVesselId, setSelectedVesselId] = useState<string>(task?.vessel_id?.toString() || '');

  // Attachments
  const [retainedAttachments, setRetainedAttachments] = useState<string[]>(() => {
    if (!task?.attachment_url) return [];
    try {
      const parsed = JSON.parse(task.attachment_url);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [task.attachment_url];
    }
  });
  const [newAttachments, setNewAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Checklists
  const [availableChecklists, setAvailableChecklists] = useState<any[]>([]);
  const [selectedChecklistIds, setSelectedChecklistIds] = useState<number[]>(
    task?.required_attachments?.map((a: any) => a.id) || []
  );
  const [searchChecklist, setSearchChecklist] = useState('');
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // Manual duration
  const [isDurationManual, setIsDurationManual] = useState<boolean>(task?.duration_is_manual || false);
  const [manualDurationHours, setManualDurationHours] = useState<string>(
    task?.manual_duration ? Math.floor(Number(task.manual_duration) / 60).toString() : ''
  );
  const [manualDurationMins, setManualDurationMins] = useState<string>(
    task?.manual_duration ? (Number(task.manual_duration) % 60).toString() : ''
  );
  const [manualStartDate, setManualStartDate] = useState<string>(
    task?.start_date ? new Date(task.start_date).toISOString().slice(0, 16) : ''
  );

  // Users for checker dropdown
  const [users, setUsers] = useState<any[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  // Fetch users, task types, clients, vessels
  useEffect(() => {
    axiosInstance.get('/users').then(r => setUsers(r.data)).catch(() => {});
    axiosInstance.get('/settings/task-types').then(r => setTaskTypes(r.data)).catch(() => {});
    axiosInstance.get('/clients').then(r => setClients(r.data)).catch(() => {});
    axiosInstance.get('/vessels').then(r => setVessels(r.data)).catch(() => {});
  }, []);

  // Fetch checklists when task type changes
  useEffect(() => {
    if (!taskType) return;
    const fetchChecklists = async () => {
      try {
        const typesRes = await axiosInstance.get('/settings/task-types');
        const currentType = typesRes.data.find((tt: any) => tt.type === taskType);
        if (currentType) {
          const checkRes = await axiosInstance.get(`/task-checklists?task_type_id=${currentType.id}`);
          const active = checkRes.data.filter((c: any) => c.is_active !== false);
          setAvailableChecklists(active);
          // Auto-open groups that have pre-selected items
          const preSelectedIds: number[] = taskType === task?.task_type
            ? (task?.required_attachments?.map((a: any) => a.id) || [])
            : [];
          const groupsToOpen = active
            .filter((c: any) => preSelectedIds.includes(c.id))
            .map((c: any) => c.group_name || (t.common?.others || 'Lainnya'));
          setOpenGroups(Array.from(new Set(groupsToOpen)));
          setSelectedGroups(Array.from(new Set(groupsToOpen)));
        }
      } catch (err) {
        console.error('Failed to fetch checklists:', err);
      }
    };
    fetchChecklists();
  }, [taskType]);

  // Derived: grouped checklists for display
  const selectedTaskTypeObj = React.useMemo(
    () => taskTypes.find((tt: any) => tt.type === taskType),
    [taskTypes, taskType]
  );
  const showClientField = selectedTaskTypeObj?.show_client ?? false;
  const showVesselField = selectedTaskTypeObj?.show_vessel ?? false;

  const filteredChecklists = React.useMemo(() => {
    if (!searchChecklist.trim()) return availableChecklists;
    const lower = searchChecklist.toLowerCase();
    return availableChecklists.filter(c =>
      c.title?.toLowerCase().includes(lower) || c.group_name?.toLowerCase().includes(lower)
    );
  }, [availableChecklists, searchChecklist]);

  const groupedChecklists = React.useMemo(() => {
    const othersLabel = t.common?.others || 'Lainnya';
    const groups: { [key: string]: any[] } = {};
    filteredChecklists.forEach(c => {
      const g = c.group_name || othersLabel;
      if (!groups[g]) groups[g] = [];
      groups[g].push(c);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === othersLabel) return 1;
      if (b === othersLabel) return -1;
      return a.localeCompare(b);
    });
    return sortedKeys.map(key => ({ group_name: key, attachments: groups[key] }));
  }, [filteredChecklists, t.common?.others]);

  const totalDuration = React.useMemo(() => {
    const totalMins = availableChecklists
      .filter(c => selectedChecklistIds.includes(c.id))
      .reduce((sum, c) => sum + (Number(c.duration) || 0), 0);
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} ${language === 'id' ? 'Hari' : 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${language === 'id' ? 'Jam' : 'Hours'}`);
    if (mins > 0) parts.push(`${mins} ${language === 'id' ? 'Menit' : 'Minutes'}`);
    return parts.length > 0 ? parts.join(' ') : (language === 'id' ? '0 Menit' : '0 Minutes');
  }, [availableChecklists, selectedChecklistIds, language]);

  const toggleGroupOpen = (groupName: string) => {
    setOpenGroups(prev =>
      prev.includes(groupName) ? prev.filter(g => g !== groupName) : [...prev, groupName]
    );
  };

  const toggleGroupSelect = (groupName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const group = groupedChecklists.find(g => g.group_name === groupName);
    const ids = group?.attachments.map((a: any) => a.id) || [];
    if (selectedGroups.includes(groupName)) {
      setSelectedGroups(prev => prev.filter(g => g !== groupName));
      setSelectedChecklistIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedGroups(prev => [...prev, groupName]);
      setSelectedChecklistIds(prev => Array.from(new Set([...prev, ...ids])));
      setOpenGroups(prev => prev.includes(groupName) ? prev : [...prev, groupName]);
    }
  };

  const toggleChecklist = (id: number) => {
    setSelectedChecklistIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCheckAll = () => {
    const ids = filteredChecklists.map(c => c.id);
    setSelectedChecklistIds(prev => Array.from(new Set([...prev, ...ids])));
    const groups = groupedChecklists.map(g => g.group_name);
    setSelectedGroups(prev => Array.from(new Set([...prev, ...groups])));
    setOpenGroups(prev => Array.from(new Set([...prev, ...groups])));
  };

  const handleUncheckAll = () => {
    const ids = filteredChecklists.map(c => c.id);
    setSelectedChecklistIds(prev => prev.filter(id => !ids.includes(id)));
    const groups = groupedChecklists.map(g => g.group_name);
    setSelectedGroups(prev => prev.filter(g => !groups.includes(g)));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const processed = await Promise.all(newFiles.map(async file => {
        if (file.type.startsWith('image/')) {
          try { return await compressImage(file); } catch { return file; }
        }
        return file;
      }));
      setNewAttachments(prev => [...prev, ...processed]);
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!task?.id) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('description', description);
      formData.append('is_urgent', isUrgent.toString());
      formData.append('is_maintenance', isMaintenance.toString());
      formData.append('checker_user_id', selectedChecker);
      if (taskType) formData.append('task_type', taskType);
      formData.append('client_id', showClientField ? selectedClientId : '');
      formData.append('vessel_id', showVesselField ? selectedVesselId : '');

      if (selectedChecklistIds.length > 0) {
        selectedChecklistIds.forEach(id => formData.append('task_checklist_ids', id.toString()));
      } else {
        formData.append('task_checklist_ids', '');
      }

      formData.append('duration_is_manual', isDurationManual.toString());
      if (isDurationManual) {
        const totalMins = (parseInt(manualDurationHours || '0', 10) * 60) + parseInt(manualDurationMins || '0', 10);
        formData.append('manual_duration', totalMins.toString());
        if (manualStartDate) formData.append('start_date', new Date(manualStartDate).toISOString());
      }

      formData.append('retained_attachments', JSON.stringify(retainedAttachments));

      newAttachments.forEach(file => formData.append('attachments', file));

      await axiosInstance.put(`/tasks/${task.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      showAlert(t.common.success, t.taskDetails.taskUpdated || 'Task updated successfully', 'success', () => {
        navigate(-1);
      });
    } catch (err: any) {
      showAlert(t.common.error, err?.response?.data?.message || t.taskDetails.taskUpdateFailed || 'Failed to update task', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!task) {
    return (
      <div id='screen' style={{ minHeight: '100vh', backgroundColor: '#FFFFFF' }}>
        <components.Header title={(t.taskDetails as any)?.editTask || 'Edit Task'} goBack={true} />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <text.T16 style={{ color: '#9CA3AF' }}>No task data</text.T16>
        </div>
      </div>
    );
  }

  return (
    <div id='screen' style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      {/* Header */}
      <components.Header title={(t.taskDetails as any)?.editTask || 'Edit Task'} goBack={true} />

      {/* Content */}
      <main style={{ marginTop: 62, height: 'calc(100vh - 62px)', overflowY: 'auto', paddingBottom: 100 }} className='container'>

        {/* Task Type */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>
            {(t.createTask as any)?.taskType || 'Tipe Tugas'}
          </text.H5>
          <div style={{ position: 'relative' }}>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10,
                border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF',
                fontSize: 14, outline: 'none', appearance: 'none', cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            >
              <option value="">{(t.createTask as any)?.chooseTaskType || 'Pilih Tipe'}</option>
              {taskTypes.map((tt: any) => (
                <option key={tt.id} value={tt.type}>{tt.type}</option>
              ))}
            </select>
            <ArrowIcon size={20} color="#9CA3AF" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

        {/* Client */}
        {/* Client */}
        {showClientField && (
          <div style={{ marginBottom: 16 }}>
            <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>
              {(t.createTask as any)?.client || 'Client'}
            </text.H5>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedClientId}
                onChange={(e) => { setSelectedClientId(e.target.value); setSelectedVesselId(''); }}
                style={{
                  width: '100%', padding: '12px 16px', borderRadius: 10,
                  border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF',
                  fontSize: 14, outline: 'none', appearance: 'none', cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <option value="">{(t.createTask as any)?.chooseClient || 'Pilih Client (Opsional)'}</option>
                {clients.map((c: any) => (
                  <option key={c.id} value={c.id.toString()}>{c.name}</option>
                ))}
              </select>
              <ArrowIcon size={20} color="#9CA3AF" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            </div>
          </div>
        )}

        {/* Vessel */}
        {showVesselField && (
          <div style={{ marginBottom: 16 }}>
            <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>
              {(t.createTask as any)?.vessel || 'Vessel'}
            </text.H5>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedVesselId}
                onChange={(e) => setSelectedVesselId(e.target.value)}
                disabled={showClientField && !selectedClientId}
                style={{
                  width: '100%', padding: '12px 16px', borderRadius: 10,
                  border: '1px solid #E5E7EB',
                  backgroundColor: (!showClientField || selectedClientId) ? '#FFFFFF' : '#F9FAFB',
                  fontSize: 14, outline: 'none', appearance: 'none',
                  cursor: (!showClientField || selectedClientId) ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit', color: (!showClientField || selectedClientId) ? 'inherit' : '#9CA3AF'
                }}
              >
                <option value="">{(t.createTask as any)?.chooseVessel || 'Pilih Vessel (Opsional)'}</option>
                {vessels
                  .filter((v: any) => !showClientField || !selectedClientId || v.client_id?.toString() === selectedClientId)
                  .map((v: any) => (
                    <option key={v.id} value={v.id.toString()}>{v.name}</option>
                  ))
                }
              </select>
              <ArrowIcon size={20} color="#9CA3AF" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            </div>
          </div>
        )}

        {/* Urgent Toggle */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div
            onClick={() => setIsUrgent(!isUrgent)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px',
              backgroundColor: isUrgent ? '#FEF2F2' : '#F9FAFB', borderRadius: 8,
              border: isUrgent ? '1px solid #EF4444' : '1px solid #E5E7EB',
              cursor: 'pointer'
            }}
          >
            {isUrgent
              ? <CheckBoxIcon size={20} color="#EF4444" />
              : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />
            }
            <text.T14 style={{ color: isUrgent ? '#EF4444' : theme.colors.mainDark, fontWeight: isUrgent ? '600' : 'normal' }}>
              {t.createTask.isUrgent}
            </text.T14>
          </div>

          <div
            onClick={() => setIsMaintenance(!isMaintenance)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px',
              backgroundColor: isMaintenance ? '#EFF6FF' : '#F9FAFB', borderRadius: 8,
              border: isMaintenance ? '1px solid #3B82F6' : '1px solid #E5E7EB',
              cursor: 'pointer'
            }}
          >
            {isMaintenance
              ? <CheckBoxIcon size={20} color="#3B82F6" />
              : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />
            }
            <text.T14 style={{ color: isMaintenance ? '#3B82F6' : theme.colors.mainDark, fontWeight: isMaintenance ? '600' : 'normal' }}>
              {(t.createTask as any).isMaintenance || 'Maintenance'}
            </text.T14>
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>{t.common.description}</text.H5>
          <div style={{
            width: '100%', padding: '14px 20px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #FFEFE6',
            borderRadius: 10,
          }}>
            <textarea
              placeholder={t.createTask.descriptionPlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%', minHeight: 120,
                outline: 'none', border: 'none', resize: 'vertical',
                fontSize: 16, fontFamily: 'inherit', whiteSpace: 'pre-wrap',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* File Attachments */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>{t.createTask.attachment}</text.H5>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10 }}>
            {/* Retained existing files */}
            {retainedAttachments.map((url, index) => {
              const fileName = url.split(/[/\\]/).pop() || 'Attachment';
              const { icon, type } = getFileIcon(fileName);
              return (
                <div key={`retained-${index}`} style={{ position: 'relative', width: 100, height: 100, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                  {type === 'image' ? (
                    <img src={`${imageBaseUrl}${url.replace(/\\/g, '/')}`} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', flexDirection: 'column' }}>
                      {React.cloneElement(icon as React.ReactElement, { size: 24 })}
                      <text.T14 style={{ fontSize: 10, marginTop: 4 }} numberOfLines={1}>{fileName}</text.T14>
                    </div>
                  )}
                  <div
                    onClick={() => setRetainedAttachments(prev => prev.filter((_, i) => i !== index))}
                    style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', padding: 4, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%' }}
                  >
                    <DeleteIcon size={16} color="#EF4444" />
                  </div>
                </div>
              );
            })}

            {/* New files */}
            {newAttachments.map((file, index) => (
              <div key={`new-${index}`} style={{ position: 'relative', width: 100, height: 100, flexShrink: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                {file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', backgroundColor: '#F9FAFB', flexDirection: 'column' }}>
                    <AttachFileIcon size={24} color={theme.colors.mainColor} />
                    <text.T14 style={{ fontSize: 10, marginTop: 4 }} numberOfLines={1}>{file.name}</text.T14>
                  </div>
                )}
                <div
                  onClick={() => setNewAttachments(prev => prev.filter((_, i) => i !== index))}
                  style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', padding: 4, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%' }}
                >
                  <DeleteIcon size={16} color="#EF4444" />
                </div>
              </div>
            ))}

            {/* Add button */}
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: 100, height: 100, flexShrink: 0,
                border: '1px dashed #9CA3AF', borderRadius: 10,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#F9FAFB', cursor: 'pointer'
              }}
            >
              <AttachFileIcon size={24} color="#9CA3AF" />
              <text.T14 style={{ marginTop: 4, color: '#6B7280', fontSize: 12, textAlign: 'center' }}>
                {t.createTask?.tapToUpload || 'Add'}
              </text.T14>
            </div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx"
            multiple
          />
        </div>

        {/* Duration */}
        <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>{t.createTask.totalDuration}</text.H5>

          <div
            onClick={() => setIsDurationManual(!isDurationManual)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 10px', backgroundColor: isDurationManual ? '#F0FDF4' : '#FFF', borderRadius: 8,
              border: isDurationManual ? '1px solid #22C55E' : '1px solid #FFEFE6',
              cursor: 'pointer', marginBottom: 10
            }}
          >
            {isDurationManual
              ? <CheckBoxIcon size={20} color="#22C55E" />
              : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />
            }
            <text.T14 style={{ color: isDurationManual ? '#22C55E' : theme.colors.mainDark, fontWeight: isDurationManual ? '600' : 'normal' }}>
              {(t.createTask as any).manualDurationCheckbox || 'Input duration manually'}
            </text.T14>
          </div>

          {isDurationManual ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <text.T14 style={{ color: '#6B7280', marginBottom: 6, fontSize: 12 }}>{(t.createTask as any).startDate || 'Start Date'}</text.T14>
                <div style={{ padding: '14px 20px', backgroundColor: '#FFFFFF', border: '1px solid #FFEFE6', borderRadius: 10 }}>
                  <input
                    type="datetime-local"
                    value={manualStartDate}
                    onChange={(e) => setManualStartDate(e.target.value)}
                    style={{
                      width: '100%', outline: 'none', border: 'none', fontSize: 16,
                      fontFamily: 'inherit', color: manualStartDate ? 'inherit' : '#9CA3AF',
                    }}
                  />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <text.T14 style={{ color: '#6B7280', marginBottom: 6, fontSize: 12 }}>{(t.createTask as any).manualDurationLabel || 'Duration'}</text.T14>
                <div style={{
                  display: 'flex', gap: 8, padding: '14px 20px',
                  backgroundColor: '#FFFFFF', border: '1px solid #FFEFE6', borderRadius: 10,
                  alignItems: 'center',
                }}>
                  <input
                    type="number" min="0" placeholder="0" value={manualDurationHours}
                    onChange={(e) => setManualDurationHours(e.target.value)}
                    style={{ width: '100%', outline: 'none', border: 'none', fontSize: 16, fontFamily: 'inherit', textAlign: 'center' }}
                  />
                  <text.T14 style={{ color: '#9CA3AF', flexShrink: 0, fontSize: 12 }}>h</text.T14>
                  <div style={{ width: 1, height: 20, backgroundColor: '#E5E7EB', flexShrink: 0 }} />
                  <input
                    type="number" min="0" max="59" placeholder="0" value={manualDurationMins}
                    onChange={(e) => setManualDurationMins(e.target.value)}
                    style={{ width: '100%', outline: 'none', border: 'none', fontSize: 16, fontFamily: 'inherit', textAlign: 'center' }}
                  />
                  <text.T14 style={{ color: '#9CA3AF', flexShrink: 0, fontSize: 12 }}>m</text.T14>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '14px 20px', backgroundColor: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
              <text.T16 style={{ color: '#1D4ED8', fontWeight: 'bold' }}>{totalDuration}</text.T16>
            </div>
          )}
        </div>

        {/* Checklists */}
        {availableChecklists.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <text.H5 style={{ color: theme.colors.mainDark }}>{t.section?.requiredAttachments || 'Checklist Pekerjaan'}</text.H5>
              {filteredChecklists.length > 0 && filteredChecklists.every(c => selectedChecklistIds.includes(c.id)) ? (
                <div onClick={handleUncheckAll} style={{ cursor: 'pointer' }}>
                  <text.T14 style={{ color: '#9CA3AF', fontWeight: '600' }}>{t.createTask?.uncheckAll || 'Uncheck All'}</text.T14>
                </div>
              ) : (
                <div onClick={handleCheckAll} style={{ cursor: 'pointer' }}>
                  <text.T14 style={{ color: theme.colors.mainColor, fontWeight: '600' }}>{t.createTask?.checkAll || 'Check All'}</text.T14>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px',
                borderRadius: 10, border: '1px solid #E5E7EB',
                backgroundColor: '#FFFFFF'
              }}>
                <SearchIcon size={18} color="#9CA3AF" />
                <input
                  type="text"
                  placeholder={(t.createTask as any)?.searchChecklist || 'Cari checklist...'}
                  value={searchChecklist}
                  onChange={(e) => setSearchChecklist(e.target.value)}
                  style={{ flex: 1, outline: 'none', border: 'none', fontSize: 14, fontFamily: 'inherit' }}
                />
                {searchChecklist && (
                  <CloseIcon size={16} color="#9CA3AF" style={{ cursor: 'pointer' }} onClick={() => setSearchChecklist('')} />
                )}
              </div>
            </div>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: 10,
              maxHeight: 320, overflowY: 'auto',
              padding: 10, border: '1px solid #E5E7EB',
              borderRadius: 10, backgroundColor: '#F9FAFB'
            }}>
              {groupedChecklists.length > 0 ? groupedChecklists.map(group => {
                const isGroupSelected = selectedGroups.includes(group.group_name);
                const isGroupOpen = openGroups.includes(group.group_name);
                return (
                  <div key={group.group_name} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div
                      onClick={() => toggleGroupOpen(group.group_name)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px', backgroundColor: '#FFF', borderRadius: 8,
                        border: isGroupSelected ? `1px solid ${theme.colors.mainColor}` : '1px solid #E5E7EB',
                        cursor: 'pointer'
                      }}
                    >
                      <div onClick={(e) => toggleGroupSelect(group.group_name, e)} style={{ flexShrink: 0 }}>
                        {isGroupSelected
                          ? <CheckBoxIcon size={24} color={theme.colors.mainColor} />
                          : <CheckBoxOutlineBlankIcon size={24} color="#9CA3AF" />
                        }
                      </div>
                      <text.T16 style={{ color: isGroupSelected ? theme.colors.mainDark : '#6B7280', flex: 1 }}>
                        {group.group_name}
                      </text.T16>
                      <ArrowIcon
                        size={20} color="#9CA3AF"
                        style={{ transform: isGroupOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease-in-out' }}
                      />
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateRows: isGroupOpen ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out',
                      opacity: isGroupOpen ? 1 : 0,
                      marginTop: isGroupOpen ? 8 : 0,
                    }}>
                      <div style={{ overflow: 'hidden' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 10 }}>
                          {group.attachments.map((att: any) => {
                            const isSelected = selectedChecklistIds.includes(att.id);
                            return (
                              <div
                                key={att.id}
                                onClick={() => toggleChecklist(att.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  padding: '10px', backgroundColor: '#FFF', borderRadius: 8,
                                  border: isSelected ? `1px solid ${theme.colors.mainColor}` : '1px solid #E5E7EB',
                                  cursor: 'pointer'
                                }}
                              >
                                {isSelected
                                  ? <CheckBoxIcon size={24} color={theme.colors.mainColor} />
                                  : <CheckBoxOutlineBlankIcon size={24} color="#9CA3AF" />
                                }
                                <text.T16 style={{ color: isSelected ? theme.colors.mainDark : '#6B7280' }}>{att.title}</text.T16>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <text.T14 style={{ color: '#9CA3AF', textAlign: 'center', marginTop: 10 }}>
                  {t.createTask?.noChecklistFound || 'Tidak ada checklist yang cocok.'}
                </text.T14>
              )}
            </div>
          </div>
        )}

        {/* Checker Dropdown */}
        <div style={{ marginBottom: 24 }}>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 10 }}>
            {(t.createTask as any)?.checker || 'Pemeriksa (Opsional)'}
          </text.H5>
          <div style={{ position: 'relative' }}>
            <select
              value={selectedChecker}
              onChange={(e) => setSelectedChecker(e.target.value)}
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 10,
                border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF',
                fontSize: 14, outline: 'none', appearance: 'none', cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            >
              <option value="">{(t.createTask as any)?.chooseChecker || 'Pilih Pemeriksa'}</option>
              {users.filter(u => u.id != authSlice.userId).map(user => (
                <option key={user.id} value={user.id.toString()}>{user.name}</option>
              ))}
            </select>
            <ArrowIcon size={20} color="#9CA3AF" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

      </main>

      {/* Save Button — fixed at bottom */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        backgroundColor: 'white', padding: '16px 24px 32px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 10
      }}>
        <div className='container' style={{ padding: 0, maxWidth: 650, margin: '0 auto' }}>
          <components.Button
            title={loading ? (t.common.saving || 'Menyimpan...') : (t.common.save || 'Simpan')}
            onClick={handleSave}
          />
        </div>
      </div>

      {/* Alert Modal */}
      {alertConfig.isOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}>
            <div style={{ marginBottom: 12 }}>
              {alertConfig.type === 'success'
                ? <svg.NotificationCheckSvg />
                : alertConfig.type === 'error'
                  ? <svg.RejectedSvg />
                  : <svg.AlertSvg />
              }
            </div>
            <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
            <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor }}>
              {alertConfig.message}
            </text.T16>
            <components.Button title={t.common.ok || 'OK'} onClick={hideAlert} containerStyle={{ width: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};
